import FinalizeError from '../dto/variant/finalizeErrorTypes';
import { httpClient } from '../httpClient/httpClient';
import { AutoUpdateRequest, AutoUpdateResponse } from './autoUpdateTypes';
import { FileLink } from './filesApi';
import { ArticleAxisInfo, BranchProductDeveloper } from './articlesApi';
import { SimpleContact } from './contactsApi';
import { BranchIdentifier } from '@texas/utils/helpers/texas/branchHelpers';
import { IdNameDto } from './sharedTypes';
import { ProductApproval } from './productApprovalsApi';
import { SalesInformation } from './salesInformationApi';
import { PurchaseInformation } from './purchaseInformationApi';
import { SlimTag } from './tagsApi';

export enum ArticleProcess {
  KickOff = 1,
  SupplierInquiry = 2,
  QuotationReview = 3,
  SalesOffer = 4,
  OfferApproved = 5,
  InitiateSampling = 6,
  SamplingUnderway = 7,
  SampleSentToBc = 8,
  SampleSentToBrand = 9,
  SampleApproved = 10,
}

export interface CreateProjectData {
  customerId: number;
  keyAccountManagerId: number;
  customerName: string;
  identifier: string;
  customerArchived: boolean;
}

export interface GarpData {
  itemCode: string;
  name: string;
  branchData: GarpBranchData[];
}

interface GarpBranchData {
  branchId: number;
  state: number;
  articleProcess: ArticleProcess;
  startDate: string;
  requestDate: string;
  endDate: string;
  requestCompletedate: string;
  sampleConfirmationDate: string;
  priceListsSyncedOn: string;

  toDoTaskId: number;
  categoryCodeId: number;
  categoryCode: string;
  customsCodeId: number;
  customsCode: string;
  countryOfOriginId: number;
  countryOfOrigin: string;
  productGroupId: number;
  productGroup: string;
  techniqueId: number;
  techniqueCode: string;
  materialId: number;
  materialCode: string;
  vatCode: string;
  tierPriceId: number;
  tierPrice: string;
  salesCurrency: string;
  salesExchangeRate?: number;
  purchaseCurrency: string;
  purchaseExchangeRate?: number;

  freightCost: number;
  handlingFee: number;
  supplier: string;
  totalSalesThisYear: number;
  titalSalesLastYear: number;

  season: string;
  unit: string;

  quantityPerUnit?: number;
  weight?: number;
  decimalsInQuantity?: number;
  decimalsInPrice?: number;
  purchasePoint?: number;
  purchaseQuantity?: number;
  leadTime?: number;

  syncedCategoryCode: string;
  syncedCustomsCode: string;
  syncedCountryOfOrigin: string;
  syncedProductGroupCode: string;
  syncedTechniqueCode: string;
  syncedMaterialCode: string;
  syncedTierPriceId?: number;
  syncedSalesCurrency: string;
  syncedSalesExchangeRate: number;
  syncedPurchaseCurrency: string;
  syncedPurchaseExchangeRate?: number;
  syncedFreightCost?: number;
  syncedHandlingFee?: number;
  syncedSupplier: string;
  syncedSeason: string;
  syncedDecimalsInQuantity?: number;
  syncedDecimalsInPrice?: number;
  syncedPurchasePoint?: number;
  syncedPurchaseQuantity?: number;
  syncedLeadTime?: number;
  syncedUnit: string;
  syncedQuantityPerUnit?: number;
  syncedWeight?: number;

  hasChanged: boolean;
}

export interface FinalizeRequest {
  branchIds?: number[];
}

export interface ArticleBranch {
  branchId: number;
  identifier: string;
  branchName: string;
  lastSync?: string;
}

export interface VariantNavigationSummary {
  branches: ArticleBranch[];
  optionCount: number;
  versionCount: number;
  supplierCount: number;
  openTaskCount: number;
  openInquiryCount: number;
  sentInquiryCount: number;
  draftOfferCount: number;
}

export enum FieldValueType {
  Percentage = 1,
  Text = 2,
}

export interface ArticleBranchErpSyncChanges {
  branchId: number;
  modifications: ErpSyncedModifications[];
}

interface ErpSyncedModifications {
  label: string;
  modifiedFields: ModifiedSyncedErpField[];
}

interface ModifiedSyncedErpField {
  name: string;
  syncedValue: any;
  value: any;
  type: FieldValueType;
}

export interface Branch {
  name: string;
  id: number;
  identifier: BranchIdentifier;
}

export interface VariantSuppliersOverview {
  suppliers: VariantBranchSupplier[];
  branches: Branch[];
}

export interface VariantBranchSupplier {
  id: number;
  variantId: number;
  supplierId: number;
  supplierName: string;
  tags?: SlimTag[];
  branchId: number;
  isMain: boolean;
  disabled: boolean;
  mirrorSupplier: { id: number; name: string; branch: string } | null;
  mirroringSuppliers: { id: number; name: string; branch: string }[];
  contacts: SimpleContact[];
  purchaseInformationId: number | null;
  currencyCode: string | null;
  exchangeRate: number | null;
  pendingProductApprovalCount?: number;
  acceptedProductApprovalCount?: number;
  pendingInquiryCount?: number;
  acceptedInquiryCount?: number;
  versionCount?: number;
}

export interface AutoUpdateInfoRequest {
  name: string;
  notes: string;
}

export interface VariantInfo {
  id: number;
  name: string;
  notes: string;
  productGroupId?: number;
  productGroupName?: string;
  optionLinkId: number | null;
  isProductDevelopment: boolean;
  branches: IdNameDto[];
}

export interface VariantFilesInfo {
  id: number;
  folderId: number;
  artworkImageId?: number;
  artworkImageIdentifier?: string;
  artworkImageArchived: boolean;
  artworkDocumentId?: number;
  artworkDocument: FileLink | null;
}

export interface VariantBranchOverview {
  mainPurchaseInfo: PurchaseInformation | null;
  salesInfo: SalesInformation | null;
  linkedOption: CompositionGroupLink | null;
  productApprovals: ProductApproval[];
  garpBranchProductApproval: ProductApproval | null;
  productDevelopmentVariantId: number | null;
  acceptedProductApprovalsCount: number;
  pendingProductApprovalsCount: number;
  isMatrixArticle: boolean;
}

export interface CompositionGroupLink {
  id?: number;
  linkedVariantId?: number;
  note: string;
  version: number;
  isReady: boolean;
}

export interface AddSupplierBranchRequest {
  supplierId: number;
  branchId: number;
  contactIds: number[];
  optionIds?: number[];
}

export interface CreatePurchaseInformationRequest {
  supplierId: number;
  branchId: number;
}
export interface UpdateSupplierContactsRequest {
  contactIds: number[];
}

export interface SupplierContact {
  name: string;
  email: string;
  id: number;
}

export interface VariantDataAvailability {
  hasSuppliers: boolean;
  hasPurchasePrices: boolean;
  hasSalesPrices: boolean;
  hasGarpData: boolean;
}

export interface VariantPhoto {
  id: number | null;
  identifier: string | null;
  archived: boolean;
}

export const variantsApi = {
  validateVariant: (variantId: number) =>
    httpClient.get<FinalizeError[]>(`variants/${variantId}/validate`),
  finalize: (variantId: number, request: FinalizeRequest) =>
    httpClient.post<GarpData>(`variants/${variantId}/finalize`, {
      data: request,
    }),
  getArticleBranches: (variantId: number) =>
    httpClient.get<ArticleBranch[]>(`variants/${variantId}/branches`),
  getNavigationSummary: (variantId: number, branchIds?: number[]) =>
    httpClient.get<VariantNavigationSummary>(
      `variants/${variantId}/navigation-summary`,
      { params: { branchIds } },
    ),
  getSyncChanges: (variantId: number) =>
    httpClient.get<ArticleBranchErpSyncChanges[]>(
      `variants/${variantId}/changes`,
    ),
  getSuppliers: (variantId: number) =>
    httpClient.get<VariantBranchSupplier[]>(`variants/${variantId}/suppliers`),
  getVariantBranchSuppliers: (variantId: number, includeDisabled: boolean) =>
    httpClient.get<VariantSuppliersOverview>(
      `variants/${variantId}/suppliers/overview`,
      { params: { includeDisabled } },
    ),
  addSupplierBranch: (variantId: number, request: AddSupplierBranchRequest) =>
    httpClient.post<VariantBranchSupplier>(`variants/${variantId}/suppliers`, {
      data: request,
    }),
  updateSupplierContacts: (
    variantSupplierId: number,
    request: UpdateSupplierContactsRequest,
  ) =>
    httpClient.put(`variants/suppliers/${variantSupplierId}/contacts`, {
      data: request,
    }),
  getSupplierContacts: (variantSupplierId: number) =>
    httpClient.get<SupplierContact[]>(
      `variants/suppliers/${variantSupplierId}/contacts`,
    ),
  disableSupplierBranch: (variantSupplierId: number) =>
    httpClient.put(`variants/suppliers/${variantSupplierId}/disable`),
  enableSupplierBranch: (variantSupplierId: number) =>
    httpClient.put(`variants/suppliers/${variantSupplierId}/enable`),
  mirrorSupplier: (variantSupplierId: number, toMirrorId: number) =>
    httpClient.put(
      `variants/suppliers/${variantSupplierId}/mirror/${toMirrorId}`,
    ),
  removeMirror: (variantSupplierId: number) =>
    httpClient.delete(`variants/suppliers/${variantSupplierId}/mirror`),
  createPurchaseInformation: (
    variantId: number,
    request: CreatePurchaseInformationRequest,
  ) => httpClient.post(`variants/${variantId}/purchase`, { data: request }),
  getVariantInfo: (variantId: number) =>
    httpClient.get<VariantInfo>(`variants/${variantId}/info`),
  autoUpdateInfo: (
    request: AutoUpdateRequest<AutoUpdateInfoRequest>,
    variantId: number,
  ) =>
    httpClient.put<AutoUpdateResponse<VariantInfo>>(
      `variants/auto/${variantId}/info`,
      { data: request },
    ),
  getVariantFilesInfo: (variantId: number) =>
    httpClient.get<VariantFilesInfo>(`variants/${variantId}/files-info`),
  getVariantPhoto: (variantId: number) =>
    httpClient.get<VariantPhoto>(`variants/${variantId}/photo-info`),
  getCellPhoto: (
    variantId: number,
    matrixNodeValueXId: number,
    matrixNodeValueYId: number,
  ) =>
    httpClient.get<VariantPhoto>(
      `variants/${variantId}/cell-photo-info?matrixNodeValueXId=${matrixNodeValueXId}&matrixNodeValueYId=${matrixNodeValueYId}`,
    ),
  updateArtworkDocument: (variantId: number, documentId: number) =>
    httpClient.put(`variants/${variantId}/artworkdocument/${documentId}`),
  deleteArtworkDocument: (variantId: number) =>
    httpClient.delete(`variants/${variantId}/artworkdocument`),
  updateArtworkImage: (variantId: number, imageId: number) =>
    httpClient.put(`variants/${variantId}/artworkimage/${imageId}`),
  deleteArtworkImage: (variantId: number) =>
    httpClient.delete(`variants/${variantId}/artworkimage`),
  updatePhoto: (
    variantId: number,
    photoId: number,
    matrixNodeValueXId?: number,
    matrixNodeValueYId?: number,
  ) =>
    httpClient.put(`variants/${variantId}/photo/${photoId}`, {
      params: { matrixNodeValueXId, matrixNodeValueYId },
    }),
  deletePhoto: (
    variantId: number,
    matrixNodeValueXId?: number,
    matrixNodeValueYId?: number,
  ) =>
    httpClient.delete(`variants/${variantId}/photo`, {
      params: { matrixNodeValueXId, matrixNodeValueYId },
    }),
  getAxisInfo: (id: number) =>
    httpClient.get<ArticleAxisInfo>(`variants/${id}/matrix/axis`),
  getVariantDataAvailability: (variantId: number) =>
    httpClient.get<VariantDataAvailability>(
      `variants/${variantId}/data-availability`,
    ),
  getVariantProductDevelopers: (variantId: number) =>
    httpClient.get<BranchProductDeveloper[]>(
      `variants/${variantId}/product-developers`,
    ),
  getBranchOverview: (variantId: number, branchId: number) =>
    httpClient.get<VariantBranchOverview>(
      `variants/${variantId}/branches/${branchId}/overview`,
    ),
  downloadExcel: (variants: { variantId: number; branchId: number }[]) =>
    httpClient.post<any>(`excel/variants/export`, {
      data: variants,
      responseType: 'blob',
    }),
  downloadExcelSlim: (variantIds: number[]) =>
    httpClient.post<any>(`excel/variants/export/slim`, {
      data: { variantIds },
      responseType: 'blob',
    }),
  linkVariantToOption: (variantId: number, compositionGroupId: number) =>
    httpClient.put<string>(
      `variants/${variantId}/link-option/${compositionGroupId}`,
    ),
};
